import React from 'react';
import { Input, Select } from 'antd';
import { SearchOutlined, UserOutlined } from '@ant-design/icons';
import { INearbyStation, IRefuelAccount, IStation } from './types';
import Leftovers, { FuelsInline } from './Leftovers';
import { formatDistance } from './geo';

const { Option } = Select;

interface IFStationStep {
	accounts: IRefuelAccount[];
	account?: IRefuelAccount;
	onAccount: (id: string) => void;
	/** False hides the picker — the one account FuelData returned is used as it is. */
	accountPicker?: boolean;
	stations: IStation[];
	filtered: IStation[];
	query: string;
	/** True while a debounced query is in effect, so the count matches the list. */
	searching: boolean;
	onQuery: (value: string) => void;
	station?: IStation;
	onStation: (station: IStation) => void;
	locked?: boolean;
	nearby?: INearbyStation;
}

/** The hint under the picker when there are no leftovers to show. */
const noLeftovers = (accounts: IRefuelAccount[]) =>
	accounts.length
		? 'Немає залишків пального на цьому акаунті'
		: 'На цьому рахунку немає акаунтів із залишком пального';

const StationStep: React.FC<IFStationStep> = (props) => {
	const {
		accounts,
		account,
		accountPicker = true,
		stations,
		filtered,
		query,
		searching,
		station,
		locked,
		nearby,
	} = props;

	return (
		<div className="refuel-step">
			{accountPicker && (
				<div className="refuel-field">
					<label>Водій / Аккаунт</label>
					<Select
						placeholder="Оберіть аккаунт"
						value={account?.id}
						disabled={locked}
						onChange={props.onAccount}
						getPopupContainer={(node) => node.parentNode as HTMLElement}
						showSearch
						optionLabelProp="label"
						optionFilterProp="label"
					>
						{accounts.map((item) => (
							<Option
								key={item.id}
								value={item.id}
								label={item.name}
								className="refuel-account-option"
							>
								<span className="refuel-account-option__name">{item.name}</span>
								<span className="refuel-account-option__balance">
									<FuelsInline fuels={item.fuels} />
								</span>
							</Option>
						))}
					</Select>
					{locked ? (
						<span className="refuel-hint">
							<UserOutlined /> Спершу оберіть рахунок
						</span>
					) : (
						<Leftovers
							fuels={account?.fuels || []}
							label="Залишок"
							icon={<UserOutlined />}
							empty={noLeftovers(accounts)}
						/>
					)}
				</div>
			)}

			{/* The list is there to look at from the start; nothing in it works until
				an account is known — picked above, or on the page for app users the
				token's own, once a wallet is chosen and its leftovers are in. */}
			<div className={account && !locked ? 'refuel-stations' : 'refuel-stations locked'}>
				<Input
					allowClear
					disabled={!account || locked}
					value={query}
					onChange={(e) => props.onQuery(e.target.value)}
					prefix={<SearchOutlined />}
					placeholder="Пошук за адресою, назвою або кодом АЗК"
				/>

				<span className="refuel-count">
					{searching
						? `${filtered.length} АЗК знайдено`
						: `${stations.length} АЗК · показані всі`}
				</span>

				{filtered.length ? (
					<div className="refuel-list">
						{filtered.map((item) => {
							const near = !!nearby && item.code === nearby.station.code;
							const state = [
								'refuel-list__item',
								near ? 'nearby' : '',
								item.code === station?.code ? 'active' : '',
							]
								.filter(Boolean)
								.join(' ');
							return (
								<div key={item.code} className={state} onClick={() => props.onStation(item)}>
									<span className="refuel-list__address">{item.address}</span>
									<span className="refuel-list__name">
										{item.name} · код {item.code}
									</span>
									{near && nearby && (
										<span className="refuel-list__badge">
											Поруч з вами · {formatDistance(nearby.distance)}
										</span>
									)}
								</div>
							);
						})}
					</div>
				) : (
					<div className="refuel-status">
						<div className="refuel-status__icon empty">
							<SearchOutlined />
						</div>
						<div className="refuel-status__title">АЗК не знайдено</div>
						<div className="refuel-status__text">
							Пошук працює за адресою, назвою та кодом АЗК. Спробуйте коротший
							запит.
						</div>
					</div>
				)}
			</div>
		</div>
	);
};

export default StationStep;
